/* global Marzipano */
import { getResourcePath } from '@spotahome/soyuz/client';

const TIME_BEFORE_AUTO_ROTATING = 3000;
const GEOMETRY_WIDTH = 2000;
const DEVICE_ORIENTATION_NAME = 'deviceOrientation';

const loadMarzipano = () =>
  new Promise((resolve, reject) => {
    if (typeof Marzipano === 'undefined') {
      const script = document.createElement('script');

      // TODO: inject the path as dependency
      script.src = getResourcePath('/lib/marzipano.js');
      script.async = true;
      script.onload = () => resolve(Marzipano);
      script.onerror = () => reject('Error loading Marzipano');
      document.body.appendChild(script);
    } else {
      resolve(Marzipano);
    }
  });

const loadDeviceOrientationControlMethod = async () => {
  const orientation = await import('./DeviceOrientationControlMethod');
  return orientation.default;
};

class MarzipanoConnector {
  constructor(photos360Data, gyroscope = false) {
    this.photos360Data = photos360Data;
    this.gyroscope = gyroscope;
  }

  initialize = async domRef => {
    this.scenes = {};

    try {
      await this.initializeMarzipano(domRef);

      if (this.gyroscope) {
        await this.initializeGyroscope();
      } else {
        this.enableAutorotate();
      }
    } catch (err) {
      throw err;
    }
  };

  initializeMarzipano = async domRef => {
    try {
      this.Marzipano = await loadMarzipano();
    } catch (err) {
      throw err;
    }

    this.viewer = new this.Marzipano.Viewer(domRef);
    this.controls = this.viewer.controls();

    this.geometry = new this.Marzipano.EquirectGeometry([
      { width: GEOMETRY_WIDTH }
    ]);

    const limiter = this.Marzipano.RectilinearView.limit.traditional(
      1024,
      90 * (Math.PI / 180)
    );
    this.view = new this.Marzipano.RectilinearView({ yaw: Math.PI }, limiter);
  };

  initializeGyroscope = async () => {
    try {
      const DeviceOrientationControlMethod =
        await loadDeviceOrientationControlMethod();
      this.orientationMethod = new DeviceOrientationControlMethod();
      this.controls.registerMethod(
        DEVICE_ORIENTATION_NAME,
        this.orientationMethod
      );
      this.enableOritentationMethod();
    } catch (err) {
      throw err;
    }
  };

  enableOritentationMethod = () => {
    this.orientationMethod.getPitch((err, pitch) => {
      if (!err) {
        this.view.setPitch(pitch);
      }
    });

    this.controls.enableMethod(DEVICE_ORIENTATION_NAME);
  };

  disableOrientationMethod = () => {
    this.controls.disableMethod(DEVICE_ORIENTATION_NAME);
  };

  enableAutorotate = () => {
    const autorotateConfig = this.Marzipano.autorotate({
      yawSpeed: 0.1, // Yaw rotation speed
      targetPitch: 0.2, // Pitch value to converge to
      targetFov: Math.PI / 2 // Fov value to converge to
    });

    // Autorotate will start after 3s of idle time
    this.viewer.setIdleMovement(TIME_BEFORE_AUTO_ROTATING, autorotateConfig);
  };

  switchScene = (locationId, photoSize) => {
    let scene = this.getScene(locationId, photoSize);

    if (!scene) {
      scene = this.createScene(locationId, photoSize);
    }

    scene.switchTo();
  };

  createScene = (locationId, photoSize) => {
    const photo = this.photos360Data.find(
      photo360 => photo360.id === locationId
    );
    const photoUrl = photo.src[photoSize];

    if (!photoUrl) {
      throw new Error('The requested photo does not exist');
    }

    const source = this.Marzipano.ImageUrlSource.fromString(photoUrl);

    const { geometry, view } = this;

    const scene = this.viewer.createScene({
      source,
      geometry,
      view,
      pinFirstLevel: true
    });

    this.storeScene(scene);

    return scene;
  };

  getScene = (locationId, photoSize) => {
    const sceneId = this.generateSceneId(locationId, photoSize);

    return this.scenes[sceneId];
  };

  storeScene = (locationId, photoSize, scene) => {
    const sceneId = this.generateSceneId(locationId, photoSize);
    this.scenes[sceneId] = scene;
  };

  generateSceneId = (locationId, photoSize) => `${locationId}-${photoSize}`;
}

export default MarzipanoConnector;
